/**
 * Pestaña "Resumen" de /finanzas-empresa.
 *
 * Consolida ingresos, egresos y utilidad neta de Aloja Fácil Empresa a
 * partir de los 7 dominios de la página. Las fórmulas de qué cuenta como
 * ingreso/egreso están en docs/flujo-financiero.md §4.
 *
 * Modo mes: serie mensual (últimos 12) + utilidad acumulada del año.
 * Modo rango: totales únicos del rango seleccionado.
 */

import React from "react";
import {
  Alert,
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { FiltroFecha } from "./filtroGlobalFecha";
import {
  useResumenLogic,
  ResumenPorPeriodo,
  ResumenTotales,
} from "./useResumenLogic";
import { ahoraChile } from "../../utils/fechaChile";

dayjs.locale("es");

// ─── Formatters ─────────────────────────────────────────────────────────────

const clp = (v: number | null | undefined) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);
};

const clpOrDash = (v: number | null | undefined) => {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return "—";
  return clp(n);
};

// ─── Tipos locales ──────────────────────────────────────────────────────────

interface FilaDesglose {
  key: string;
  categoria: string;
  ayuda: string;
  ingreso: number;
  egreso: number;
  saldo: number;
  esTotal?: boolean;
}

interface Props {
  filtroFecha?: FiltroFecha;
  /**
   * Contador de refresco manual. Al cambiar (botón "Actualizar" del header)
   * fuerza a `useResumenLogic` a re-consultar el backend sin tocar el mes.
   */
  refreshKey?: number;
}

// ─── Desglose (común a modo mes y rango) ────────────────────────────────────

function construirDesglose(totales: ResumenTotales): FilaDesglose[] {
  const filas: FilaDesglose[] = [
    {
      key: "arriendos",
      categoria: "Arriendos",
      ayuda:
        "Suma de ingreso_af_clp de cada reserva. Ya incluye comisiones AF + IVA + transporte + materiales + lavandería (cuando la propiedad usa Lavandería externa) − descuentos solo AF. Doc §4.",
      ingreso: totales.arriendos.ingreso,
      egreso: 0,
      saldo: totales.arriendos.ingreso,
    },
    {
      key: "aseos",
      categoria: "Aseos",
      ayuda:
        "Egreso = sueldo del aseador por cada limpieza que paga AF (pagado_por tipo 0). Ingreso = transporte + materiales que AF retiene cuando el aseo lo paga el Cliente o el Arrendatario (pagado_por tipo 1/2). Bonos extra, multas y adelantos van en la categoría Sueldos / Gastos.",
      ingreso: totales.aseos.ingreso,
      egreso: totales.aseos.egreso,
      saldo: totales.aseos.ingreso - totales.aseos.egreso,
    },
    // G12 (round 2026-06-20): se eliminaron las filas "Lavandería externos" y
    // "Lavandería AF (pagos a internas)". Referenciaban las tablas `laundry` /
    // `laundry_af` (dropeadas en 2026-05-11) y SIEMPRE mostraban $0
    // (fetchTotalesPeriodo nunca las poblaba) → ruido/confusión. La lavandería
    // tiene su propia contabilidad en /lavanderia (no entra al Resumen de AF
    // Empresa, ver flujo-financiero.md §7 y useResumenLogic). Las claves
    // siguen en ResumenTotales por compat, simplemente no se renderizan.
    {
      key: "sueldos",
      categoria: "Sueldos",
      ayuda:
        "Expenses categoria='sueldo' del período. Sueldos administrativos y operativos pagados por la empresa.",
      ingreso: totales.sueldos.ingreso,
      egreso: totales.sueldos.egreso,
      saldo: totales.sueldos.ingreso - totales.sueldos.egreso,
    },
    {
      key: "gastos",
      categoria: "Gastos operacionales",
      ayuda:
        "Expenses sin categoria sueldo (oficina, servicios, marketing, etc.). El Ingreso son los flujos donde AF recibe plata (rol='recibe', ej. un flujo 'Ingreso' o la recuperación de inventario).",
      ingreso: totales.gastos.ingreso,
      egreso: totales.gastos.egreso,
      saldo: totales.gastos.ingreso - totales.gastos.egreso,
    },
    {
      key: "productos",
      categoria: "Productos (compras)",
      ayuda:
        "Compras de inventario del período (tipo='compra'). Los movimientos internos bodega → propiedad NO son egreso de caja — son transferencias.",
      ingreso: totales.productos.ingreso,
      egreso: totales.productos.egreso,
      saldo: totales.productos.ingreso - totales.productos.egreso,
    },
  ];

  const ingresoTotal = filas.reduce((a, f) => a + f.ingreso, 0);
  const egresoTotal = filas.reduce((a, f) => a + f.egreso, 0);
  filas.push({
    key: "__total",
    categoria: "TOTAL",
    ayuda: "Ingresos − Egresos = Utilidad neta del período.",
    ingreso: ingresoTotal,
    egreso: egresoTotal,
    saldo: ingresoTotal - egresoTotal,
    esTotal: true,
  });

  return filas;
}

// ─── Columnas tabla desglose ────────────────────────────────────────────────

const desgloseColumns: ColumnsType<FilaDesglose> = [
  {
    title: "Categoría",
    dataIndex: "categoria",
    key: "categoria",
    render: (v: string, row) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontWeight: row.esTotal ? 700 : 500 }}>{v}</span>
        {!row.esTotal && (
          <Tooltip title={row.ayuda}>
            <InfoCircleOutlined style={{ opacity: 0.6, fontSize: 12 }} />
          </Tooltip>
        )}
      </span>
    ),
  },
  {
    title: "Ingreso",
    dataIndex: "ingreso",
    key: "ingreso",
    align: "right",
    width: 200,
    render: (v: number, row) => (
      <span style={{ fontWeight: row.esTotal ? 700 : 400, color: "#237804" }}>
        {clpOrDash(v)}
      </span>
    ),
  },
  {
    title: "Egreso",
    dataIndex: "egreso",
    key: "egreso",
    align: "right",
    width: 200,
    render: (v: number, row) => (
      <span style={{ fontWeight: row.esTotal ? 700 : 400, color: "#a8071a" }}>
        {clpOrDash(v)}
      </span>
    ),
  },
  {
    // Round 2026-05-08: cambiado de "Saldo" → "Utilidad" por pedido del
    // usuario. La columna sigue siendo `ingreso - egreso` por categoría;
    // solo cambia la etiqueta visible. El dataIndex del row sigue
    // llamándose `saldo` para no romper el endpoint que arma la tabla.
    title: "Utilidad",
    dataIndex: "saldo",
    key: "saldo",
    align: "right",
    width: 220,
    render: (v: number, row) => {
      const color = v >= 0 ? "#237804" : "#a8071a";
      return (
        <span
          style={{
            fontWeight: row.esTotal ? 800 : 500,
            color,
            fontSize: row.esTotal ? 15 : 14,
          }}
        >
          {clp(v)}
        </span>
      );
    },
  },
];

// ─── Columnas serie mensual ─────────────────────────────────────────────────

const serieColumns: ColumnsType<ResumenPorPeriodo> = [
  {
    title: "Mes",
    dataIndex: "label",
    key: "label",
    width: 160,
    render: (v: string) =>
      v.charAt(0).toUpperCase() + v.slice(1).toLowerCase(),
  },
  {
    title: "Ingreso",
    dataIndex: "ingresoTotal",
    key: "ingresoTotal",
    align: "right",
    width: 180,
    render: (v: number) => (
      <span style={{ color: "#237804" }}>{clpOrDash(v)}</span>
    ),
  },
  {
    title: "Egreso",
    dataIndex: "egresoTotal",
    key: "egresoTotal",
    align: "right",
    width: 180,
    render: (v: number) => (
      <span style={{ color: "#a8071a" }}>{clpOrDash(v)}</span>
    ),
  },
  {
    title: "Utilidad del mes",
    dataIndex: "utilidad",
    key: "utilidad",
    align: "right",
    width: 200,
    render: (v: number) => (
      <span
        style={{
          fontWeight: 700,
          color: v >= 0 ? "#237804" : "#a8071a",
        }}
      >
        {clp(v)}
      </span>
    ),
  },
];

// ─── Componente ─────────────────────────────────────────────────────────────

export function ResumenSection({ filtroFecha, refreshKey }: Props) {
  const {
    loading,
    error,
    totales,
    ingresoTotal,
    egresoTotal,
    utilidad,
    porMes,
    utilidadAcumuladaAnio,
  } = useResumenLogic(filtroFecha, refreshKey);

  const desglose = React.useMemo(
    () => construirDesglose(totales),
    [totales],
  );

  const esModoMes =
    !filtroFecha ||
    filtroFecha.modo === "mes" ||
    filtroFecha.modo === "anterior";

  // Etiqueta legible del período activo.
  const etiquetaPeriodo = React.useMemo(() => {
    const ff = filtroFecha ?? { modo: "mes" as const, mes: ahoraChile() };
    if (ff.modo === "rango" && ff.rango) {
      return `${ff.rango[0].format("DD-MM-YYYY")} al ${ff.rango[1].format("DD-MM-YYYY")}`;
    }
    const m = ff.mes ?? ahoraChile();
    const label = m.format("MMMM YYYY");
    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [filtroFecha]);

  const utilidadColor = utilidad >= 0 ? "#237804" : "#a8071a";

  return (
    <div className="finanzas-section">
      {/* ── Alerta con explicación general ─────────────────────────────────── */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Cómo se calcula la utilidad de Aloja Fácil Empresa"
        description={
          <div style={{ fontSize: 13, lineHeight: 1.55 }}>
            <p style={{ margin: 0 }}>
              <strong>Ingresos empresa</strong> = suma de{" "}
              <code>ingreso_af_clp</code> de todas las reservas del período.
              Este campo ya consolida comisiones AF + IVA + transporte +
              materiales + lavandería (cuando la propiedad usa "Lavandería
              General Externa") − descuentos solo AF. Por eso la lavandería
              externa no suma ingresos aparte.
            </p>
            <p style={{ margin: "6px 0 0" }}>
              <strong>Egresos empresa</strong> = sueldos (expenses categoría
              sueldo) + gastos operacionales + sueldos de aseadores + compras de
              inventario. La <strong>lavandería NO entra</strong> como bucket del
              Resumen: tiene su contabilidad en <em>/lavanderia</em>; el subítem
              lavandería ya está dentro del ingreso de arriendos, y el costo real
              de una lavandería externa pagado a mano entra como gasto operacional.
            </p>
            <p style={{ margin: "6px 0 0" }}>
              <strong>Utilidad neta</strong> = Ingresos − Egresos. Ver{" "}
              <em>docs/flujo-financiero.md §4</em>.
            </p>
          </div>
        }
      />

      {/* ── KPI cards ──────────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading} variant="outlined">
            <Statistic
              title={
                <span>
                  Ingreso total empresa{" "}
                  <Tooltip title="Σ ingresos de todas las categorías: arriendos (ingreso_af_clp) + transporte/materiales de aseos pagados por cliente/arrendatario + flujos donde AF recibe (rol='recibe', incl. recuperación de inventario)">
                    <InfoCircleOutlined style={{ opacity: 0.6 }} />
                  </Tooltip>
                </span>
              }
              value={ingresoTotal}
              precision={0}
              valueStyle={{ color: "#237804" }}
              prefix="$"
              groupSeparator="."
              suffix="CLP"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card loading={loading} variant="outlined">
            <Statistic
              title={
                <span>
                  Egreso total empresa{" "}
                  <Tooltip title="Sueldos + gastos operacionales + sueldos de aseadores + compras de inventario (la lavandería no entra al Resumen — ver /lavanderia)">
                    <InfoCircleOutlined style={{ opacity: 0.6 }} />
                  </Tooltip>
                </span>
              }
              value={egresoTotal}
              precision={0}
              valueStyle={{ color: "#a8071a" }}
              prefix="$"
              groupSeparator="."
              suffix="CLP"
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} lg={8}>
          <Card
            loading={loading}
            variant="outlined"
            style={{
              borderColor: utilidad >= 0 ? "#b7eb8f" : "#ffa39e",
              background: utilidad >= 0 ? "#f6ffed" : "#fff1f0",
            }}
          >
            <Statistic
              title={
                <span style={{ fontWeight: 600 }}>
                  Utilidad neta {etiquetaPeriodo}
                </span>
              }
              value={utilidad}
              precision={0}
              valueStyle={{
                color: utilidadColor,
                fontWeight: 700,
                fontSize: 28,
              }}
              prefix="$"
              groupSeparator="."
              suffix="CLP"
            />
            {esModoMes && porMes.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#555" }}>
                Utilidad acumulada del año:{" "}
                <strong
                  style={{
                    color: utilidadAcumuladaAnio >= 0 ? "#237804" : "#a8071a",
                  }}
                >
                  {clp(utilidadAcumuladaAnio)}
                </strong>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Error al cargar el resumen"
          description={error}
        />
      )}

      {/* ── Tabla desglose por categoría ───────────────────────────────────── */}
      <Card
        title={
          <span>
            Desglose por categoría — {etiquetaPeriodo}{" "}
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {esModoMes ? "Mes" : "Rango"}
            </Tag>
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Table<FilaDesglose>
          rowKey="key"
          loading={loading}
          dataSource={desglose}
          columns={desgloseColumns}
          pagination={false}
          size="middle"
          rowClassName={(r) => (r.esTotal ? "resumen-total-row" : "")}
        />
      </Card>

      {/* ── Serie mensual (solo en modo mes) ───────────────────────────────── */}
      {esModoMes && (
        <Card
          title={
            <span>
              Evolución mensual (últimos 12 meses){" "}
              <Tooltip title="Cada fila es un mes calendario. La utilidad acumulada del año suma sólo los meses del año en curso del período seleccionado.">
                <InfoCircleOutlined style={{ opacity: 0.6 }} />
              </Tooltip>
            </span>
          }
        >
          <Table<ResumenPorPeriodo>
            rowKey="label"
            loading={loading}
            dataSource={porMes}
            columns={serieColumns}
            pagination={false}
            size="middle"
            summary={(pageData) => {
              const totIng = pageData.reduce((a, r) => a + r.ingresoTotal, 0);
              const totEgr = pageData.reduce((a, r) => a + r.egresoTotal, 0);
              const totUt = pageData.reduce((a, r) => a + r.utilidad, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>Total 12 meses</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong style={{ color: "#237804" }}>{clp(totIng)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong style={{ color: "#a8071a" }}>{clp(totEgr)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong
                      style={{
                        color: totUt >= 0 ? "#237804" : "#a8071a",
                        fontSize: 15,
                      }}
                    >
                      {clp(totUt)}
                    </strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      )}

      {!esModoMes && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          En modo rango se muestra sólo el total consolidado del rango. Cambia
          a modo "Mes" en el filtro global de arriba para ver la evolución
          mensual de los últimos 12 meses.
        </Typography.Paragraph>
      )}

      {/* Fila TOTAL destacada */}
      <style>{`
        .resumen-total-row td {
          background: #fafafa !important;
          border-top: 2px solid #d9d9d9 !important;
        }
      `}</style>
    </div>
  );
}
